import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { BUILT_IN_CHART_TYPES } from '../../../constants';
import { getChartFormByType } from '@/reducers/dashboardChartsReducer';
import { BasePythonPanelFooter } from './BasePythonPanelFooter';
import { getURLQueryParams } from '@shared/utils/url';
import useUpdatePythonLibraryTemplate from '@/experiment-management-shared/api/library-panel/useUpdateCodeTemplate';
import { NEW_TEMPLATE } from '@/reducers/visualizationsReducer';
import { stringifyJSON } from '@shared/utils/jsonHelpers';
import usePanelTemplate from '@/experiment-management-shared/api/library-panel/usePanelTemplate';
import { Button } from '@ds';
import { createLibraryTemplateInstance } from '@/experiment-management-shared/helpers';
import {
  GALLERY_TAB,
  SELECT_PANEL_PATH
} from '@/experiment-management-shared/constants/visualizationConstants';
import {
  TEMPLATE_KEYS,
  PANEL_CODE_LANGUAGES
} from '@/experiment-management-shared/constants/chartsGallery';
import { useHistory } from 'react-router-dom';

interface PythonPanelFooterPythonLibraryProps {
  handleReset: () => void;
  hideGoBackButton?: boolean;
  onAddCustomPanel: (chart: object, chartType: string) => void;
  onEditCustomPanel: (chartId: string, instance: object) => void;
  projectId: string;
  chartId: string;
  hasFormBeenUpdated: boolean;
  resetInitialChartState: () => void;
  handleCloseModal: () => void;
}

const LibraryPanelFooter = ({
  handleReset,
  hideGoBackButton,
  onAddCustomPanel,
  onEditCustomPanel,
  projectId,
  chartId,
  hasFormBeenUpdated,
  resetInitialChartState,
  handleCloseModal
}: PythonPanelFooterPythonLibraryProps) => {
  const chartType = BUILT_IN_CHART_TYPES.libraryPanel;
  const chartForm = useSelector(state =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getChartFormByType(state, { chartType })
  );

  const { hash } = useLocation();
  const queryParams = getURLQueryParams(hash);

  const { viewId } = useParams<{ viewId: string }>();
  const templateId = queryParams.get('templateId') || '';
  const revisionId = queryParams.get('revisionId');
  const isPreview = queryParams.get('isPreview') === 'true';
  const isGalleryEditMode = queryParams.get('isEditMode') === 'true';
  const history = useHistory();
  const isExistingChart = !!chartId;
  const { data: template } = usePanelTemplate({
    templateId
  });

  const isJSPanel = template?.code?.type === PANEL_CODE_LANGUAGES.JAVASCRIPT;

  const saveBtnMutation = useUpdatePythonLibraryTemplate(template);
  const doneBtnMutation = useUpdatePythonLibraryTemplate(template);

  const navigateToPanelLibrary = () => {
    history.push({
      hash: `#${SELECT_PANEL_PATH}?${GALLERY_TAB}=${TEMPLATE_KEYS.PANEL_LIBRARY}`
    });
  };

  const updatedTemplate = {
    templateName: chartForm.chartName,
    code: {
      ...NEW_TEMPLATE.code,
      description: chartForm.description,
      pyCode: chartForm.code,
      internalResources: [],
      pyConfig: stringifyJSON(chartForm.code)!,
      defaultConfig: stringifyJSON(NEW_TEMPLATE.code.defaultConfig)!
    }
  };

  const updatedInstance = createLibraryTemplateInstance({
    instanceName: chartForm.instanceName,
    templateRevisionId: revisionId || '',
    projectId,
    templateId,
    viewId
  });

  const onAddPanel = () => {
    onAddCustomPanel(updatedInstance, BUILT_IN_CHART_TYPES.libraryPanel);
    handleCloseModal();
  };

  const handleSaveBtnClicked = () => {
    saveBtnMutation.mutate(updatedTemplate, {
      onSuccess: () => {
        onEditCustomPanel(chartId, updatedInstance);
        resetInitialChartState();
      }
    });
  };

  const handleSuccessfulTemplateUpdate = () => {
    if (isExistingChart) {
      onEditCustomPanel(chartId, updatedInstance);
      return handleCloseModal();
    }

    if (isGalleryEditMode) return navigateToPanelLibrary();
    onAddPanel();
  };

  const onDoneWithoutUpdate = () => {
    if (isGalleryEditMode) return navigateToPanelLibrary();
    if (isExistingChart) return handleCloseModal();
    onAddPanel();
  };

  const handleDoneBtnClicked = () => {
    if (!hasFormBeenUpdated) {
      return onDoneWithoutUpdate();
    }

    doneBtnMutation.mutate(updatedTemplate, {
      onSuccess: handleSuccessfulTemplateUpdate
    });
  };

  const renderSaveBtn = () =>
    !isPreview && (
      <Button
        size="large"
        type="tertiary"
        disabled={!hasFormBeenUpdated}
        onClick={handleSaveBtnClicked}
        loading={saveBtnMutation.isLoading}
      >
        Save
      </Button>
    );

  return (
    <BasePythonPanelFooter
      actions={{
        onReset: handleReset,
        onDone: handleDoneBtnClicked,
        onAdd: onAddPanel
      }}
      isDoneBtnLoading={doneBtnMutation.isLoading}
      renderCustomActions={renderSaveBtn}
      settings={{
        hideGoBackButton,
        isPreview,
        hasCodeBeenUpdated: hasFormBeenUpdated
      }}
      isDoneDisabled={isJSPanel}
    />
  );
};

export default LibraryPanelFooter;
